import { useState, useEffect, useCallback } from 'react';
import { getMySubscription, type SubscriptionStatus } from '../services/subscription-rest-service';
import { loggers } from '../utils/logger';

interface UseSubscriptionReturn {
  subscription: SubscriptionStatus | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  isTrialActive: boolean;
  isTrialExpiring: boolean;
  isPaidSubscription: boolean;
  canAccessFeatures: boolean;
}

/**
 * Hook to manage subscription state
 * Provides subscription data and convenient status checks
 */
export function useSubscription(): UseSubscriptionReturn {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSubscription = useCallback(async () => {
    try {
      loggers.subscriptionBilling.log('Fetching subscription...');
      setLoading(true);
      setError(null);
      const data = await getMySubscription();
      loggers.subscriptionBilling.success('Subscription data:', data);
      setSubscription(data);
    } catch (err) {
      loggers.subscriptionBilling.error('Error fetching subscription:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch subscription'));
    } finally {
      setLoading(false);
      loggers.subscriptionBilling.log('Loading complete');
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Convenient status checks
  const isTrialActive = subscription?.subscription?.plan === 'trial' && subscription?.subscription?.status === 'active';
  
  const isTrialExpiring = 
    isTrialActive && 
    subscription?.subscription?.daysRemaining !== undefined && 
    subscription.subscription.daysRemaining <= 3;
  
  const isPaidSubscription = 
    subscription?.subscription?.status === 'active' && 
    (subscription?.subscription?.plan === 'pro' || subscription?.subscription?.plan === 'studio');
  
  const canAccessFeatures = 
    subscription?.subscription?.status === 'active' && 
    (subscription?.subscription?.plan === 'trial' || subscription?.subscription?.plan === 'pro' || subscription?.subscription?.plan === 'studio');

  return {
    subscription,
    loading,
    error,
    refetch: fetchSubscription,
    isTrialActive,
    isTrialExpiring,
    isPaidSubscription,
    canAccessFeatures,
  };
}

